if (!customElements.get('banner-vehicle-compatibility')) {
  class BannerVehicleCompatibility extends HTMLElement {
    constructor() {
      super();

      this.vehicleAttributes = ['make', 'model', 'vehicle-year'];
      this.form = this.querySelector('form.make-year-model-form');
      this.button = this.querySelector('button[type="submit"]');
      this.input = this.querySelector('input.input-collection');
      this.selects = this.querySelectorAll('select.dropdown-filter');
      this.vehicleInfo = this.getVehicleInfo();
      this.bannerMessage = this.querySelector('.banner-message');
      this.resetButton = this.querySelector('button[type="reset"]');

      this.setupEventListeners();

      // There's no collection dropdown so we either need to populate
      // the filters of the entire store or the single collection chosen
      // (if only one collection has been chosen).
      this.populateCollectionFilters(true);

      this.updateCompatibilityBanner();
    }

    updateCompatibilityBanner() {
      const displayIds = ['vehicle-year', 'make', 'model']; // display order
      const selectedVehicleObj = this.vehicleInfo || {};
      const { id: selectedVehicleId, url: selectedVehicleUrl } = selectedVehicleObj;
      const bannerElement = document.querySelectorAll('banner-vehicle-compatibility');

      bannerElement?.forEach(function (element) {
        const bannerCardElement = element.querySelector('.banner-card');
        if (!bannerCardElement) return;

        const bannerTextElement = bannerCardElement.querySelector('.banner-text');

        if (selectedVehicleId) {
          const productTagList = element.querySelectorAll('tag');
          const hasMatchingTag = productTagList.length > 0 && Array.from(productTagList).some((tag) => tag.innerText === selectedVehicleId);
          const vehicleText = displayIds.map(key => selectedVehicleObj[key]?.toUpperCase().replace(/-/g, ' ')).join(' ');
          const compatibilityClass = hasMatchingTag ? 'compatible' : 'incompatible';
          const containerElement = bannerCardElement.querySelector(`.banner-text .${compatibilityClass}`);
          const contentElement = containerElement.querySelector('.saved-vehicle-name') || containerElement;

          contentElement.innerText = vehicleText;
          contentElement.href = selectedVehicleUrl;
          containerElement.classList.remove('hidden');

          // toggle classes on banner container
          bannerCardElement.classList.add(hasMatchingTag ? 'success' : 'critical');
          bannerCardElement.classList.remove(hasMatchingTag ? 'critical' : 'success', 'hidden');
        } else {
          bannerTextElement.innerHTML = '';
          bannerCardElement.classList.add('hidden');
        }
      });
    }

    reloadContent() {
      const sectionId = this.form.dataset.sectionId;
      if (!sectionId) return;
      fetch(`${window.location.pathname}?sections=${sectionId}`)
        .then(response => response.json())
        .then(sections => {
          const html = sections[sectionId];
          if (!html) return;

          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const newContent = doc.querySelector(`#shopify-section-${sectionId} banner-vehicle-compatibility`);

          const currentContent = document.querySelector(`#shopify-section-${sectionId} banner-vehicle-compatibility`);
          if (newContent && currentContent) {
            currentContent.replaceWith(newContent);
          }
        })
        .catch(error => {
          console.error('Failed to reload vehicle compatibility section', error);
        });
    }

    setupEventListeners() {
      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();

        // save searched vehicle info if it's a global search, meaning no collections are pre-selected
        if (!(this.input?.value)) {
          const vehicleData = {};
          this.selects.forEach((select) => {
            const selectedValue = select?.selectedOptions[0]?.value;
            const attribute = this.vehicleAttributes.find(attr => select.dataset.label?.toLowerCase().replace(/[\s_]+/g, '-') === attr);
            attribute && (vehicleData[attribute] = selectedValue?.toLowerCase().replace(/[\s_]+/g, '-') || '');
          });

          if (Object.keys(vehicleData).length > 0) {
            vehicleData['id'] = this.vehicleAttributes.map(attr => vehicleData[attr])?.filter(Boolean).join('-');
            vehicleData['url'] = this.form.getAttribute('action');
            this.saveVehicleInfo(vehicleData);
          }
        }

        this.reloadContent();
        this.updateCompatibilityBanner();
      });

      this.selects.forEach((event) => {
        event.addEventListener('change', this.onFilterChange.bind(this));
      });

      // when reset is clicked, clear cookie and flip the banner to form,
      this.resetButton?.addEventListener('click', (event) => {
        event.preventDefault();
        this.bannerMessage?.classList.add('hidden');
        this.resetButton?.classList.add('hidden');
        this.form?.classList.remove('hidden');
      });

      if (Shopify.designMode) {
        document.addEventListener('shopify:section:load', () => this.updateCompatibilityBanner());
        document.addEventListener('shopify:section:reorder', () => this.updateCompatibilityBanner());
      }

      // Observe changes (ex: sidebar's filter changes in facet.js) to the product grid container
      // no need to disconnect, as observer should be disposed by browser when user is navigating to other pages
      const gridContainer = document.getElementById('ProductGridContainer'); // this element id is used for the product grid update in facet.js
      if (gridContainer && !('vehicleCompatibilityMutationObserver' in gridContainer)) {
        gridContainer.vehicleCompatibilityMutationObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            // direct descendant changes
            if (mutation.type === 'childList') {
              this.updateCompatibilityBanner();
            }
          });
        });
        gridContainer.vehicleCompatibilityMutationObserver.observe(gridContainer, {
          childList: true,
          subtree: false,
          attributes: false,
          characterData: false,
        });
      }
    }

    saveVehicleInfo(vehicleData) {
      localStorage.setItem('selectedVehicle', JSON.stringify(vehicleData));
    }

    getVehicleInfo() {
      const stored = localStorage.getItem('selectedVehicle');
      return stored ? JSON.parse(stored) : null;
    }

    clearVehicleInfo() {
      localStorage.removeItem('selectedVehicle');
    }

    constructURL() {
      let collectionUrl = '/collections/all/';
      let searchParams = true;
      let urlOverride = null;

      // If the collection dropdown is present, use its value
      if (this.input?.value) {
        collectionUrl = this.input?.value;
      } else {
        // Otherwise find existing collection handle and add filter params accordingly
        let selectedMakeHandle;
        let selectedModelHandle;
        let selectedYearHandle;

        this.selects.forEach((select) => {
          const label = select.dataset.label?.toLowerCase().replace(/[\s_]+/g, '-');
          if (label === this.vehicleAttributes[0]) selectedMakeHandle = select.value.toLowerCase();
          else if (label === this.vehicleAttributes[1]) selectedModelHandle = select.value.toLowerCase().replace(/[\s_]+/g, '-');
          else if (label === this.vehicleAttributes[2]) selectedYearHandle = select.value;
        });

        // Attempt to find metaobject mapped URL (override)
        if (window.makeModelYearOverrides && typeof window.makeModelYearOverrides === 'object') {
          const makeModelYear = [selectedMakeHandle, selectedModelHandle.replaceAll('-',''), selectedYearHandle].join('-');
          if (makeModelYear in window.makeModelYearOverrides && window.makeModelYearOverrides[makeModelYear]) {
            urlOverride = new URL(window.makeModelYearOverrides[makeModelYear]);
            searchParams = false;
          }
        }

        // If override was not found - default to generating filters 
        if (urlOverride === null && window.availableCollections?.length) {
          const collectionHandles = [
            { handle: selectedMakeHandle && selectedModelHandle && selectedYearHandle && `${selectedMakeHandle}-${selectedModelHandle}-${selectedYearHandle}`, includedYear: true, includedModel: true, includedMake: true },
            { handle: selectedModelHandle && selectedYearHandle && `${selectedModelHandle}-${selectedYearHandle}`, includedYear: true, includedModel: true },
            { handle: selectedModelHandle, includedModel: true },
            { handle: selectedMakeHandle, includedMake: true }
          ].filter(item => item.handle);

          const match = collectionHandles.find(item => window.availableCollections.includes(item.handle));
          if (match) {
            collectionUrl = `/collections/${match.handle}/`;
            searchParams = match;
          }
        }
      }

      const url = urlOverride ? urlOverride : new URL(collectionUrl, window.shopUrl);

      if (searchParams) {
        this.querySelectorAll('select.dropdown-filter:enabled').forEach((select) => {
          // skip filter param if collection handle already has it
          if (select.value === '' || (select.dataset.label?.toLowerCase() === this.vehicleAttributes[0] && searchParams.includedMake) || (select.dataset.label?.toLowerCase() === this.vehicleAttributes[1] && searchParams.includedModel) || (select.dataset.label?.toLowerCase() === this.vehicleAttributes[2] && searchParams.includedYear)) {
            return;
          }

          Array.from(select.selectedOptions).forEach((option) => {
            url.searchParams.set(option.dataset.paramName, option.value);
          });
        });
      }

      return url.toString();
    }

    setFormUrl(url, background) {
      this.form?.setAttribute('action', url);

      // This operation happens on the background
      // where the user should not be notified of loading with spinners
      // etc.
      if (!background) {
        this.button.disabled = false;
      }
    }

    selectRemoveOptions(select) {
      const options = select.querySelectorAll('option:not(.default)');

      options.forEach((option) => {
        select.removeChild(option);
      });

      select.querySelector('option').selected = true;
    }

    onFilterChange(event) {
      const selectedIndex = event.target.dataset.index;

      this.selects.forEach((select) => {
        if (Number(select.dataset.index) <= Number(selectedIndex)) {
          return;
        }

        this.selectRemoveOptions(select);
        select.disabled = true;
        select.closest('.form-group').classList.add('disabled');
      });

      this.setFormUrl(this.constructURL());

      const url = new URL(this.constructURL());
      url.searchParams.set('view', 'do_not_use');
      this.toggleLoading();

      fetch(url)
        .then((response) => {
          if (!response.ok) {
            throw new Error(response.status);
          }

          return response.text();
        })
        .then((text) => {
          this.toggleLoading();
          const resultsMarkup = new DOMParser().parseFromString(text, 'text/html');

          this.selects.forEach((select) => {
            if (Number(select.dataset.index) <= Number(selectedIndex)) {
              return;
            }

            const options = resultsMarkup.querySelectorAll(`select[data-label="${select.dataset.label}"] option`);
            if (options.length > 0) {
              const availableOptions = Array.from(options).filter((option) => Number(option.dataset.count) > 0);
              select.append(...availableOptions);
              select.querySelector('option.default').selected = true;
            }

            if (Number(select.dataset.index) === Number(selectedIndex) + 1) {
              select.disabled = false;
              select.closest('.form-group').classList.remove('disabled');
            }
          });
        })
        .catch((error) => {
          throw error;
        });
    }

    populateCollectionFilters(background = false) {
      this.setFormUrl(this.constructURL(), background);

      const url = new URL(this.constructURL());
      url.searchParams.set('view', 'do_not_use');

      // When we initially populate the filters we don't want
      // to show a loading spinner.
      if (!background) {
        this.toggleLoading();
      }

      fetch(url)
        .then((response) => {
          if (!response.ok) {
            throw new Error(response.status);
          }

          return response.text();
        })
        .then((text) => {
          if (!background) {
            this.toggleLoading();
          }

          const resultsMarkup = new DOMParser().parseFromString(text, 'text/html');

          this.selects.forEach((select) => {
            const options = resultsMarkup.querySelectorAll(`select[data-label="${select.dataset.label}"] option`);
            if (options.length > 0) {
              select.append(...options);
              select.querySelector('option.default').selected = true;
            }

            if (Number(select.dataset.index) === 1) {
              select.disabled = false;
              select.closest('.form-group').classList.remove('disabled');
            }
          });
        })
        .catch((error) => {
          throw error;
        });
    }

    toggleLoading() {
      this.classList.toggle('is-loading');
      this.button.classList.toggle('loading');
    }
  }

  customElements.define('banner-vehicle-compatibility', BannerVehicleCompatibility);
}
